/**
 * Per-merchant creative brief — the only hand-written, per-brand layer.
 *
 * Everything else comes from the live snapshot (`npm run sync:brands`):
 * name, logo, photos, reward, domain, logo palette. A brand with no entry
 * here still renders (gallery order, no tagline, measured colours); a brief
 * only makes it better.
 *
 * Grounding rule: a tagline restates the merchant's own description (the
 * text they wrote in their explorer config, quoted in the comment) or their
 * logo. Never invent a claim, a stat or a testimonial. FR stays close to
 * the merchant's words; EN is a faithful translation. Keep it ≤ ~45
 * characters: it is read in ~2s.
 *
 * `shots` are 1-based positions in the brand's gallery (1 = `<slug>-hero`,
 * 2 = `<slug>-hero-2`, …), best first: shot 1 is the video's first frame,
 * i.e. its thumbnail. Skip photos with burned-in text (they fight ours).
 * Unknown positions are ignored, so a changed gallery degrades gracefully.
 * Scenes use shots 1–3 (payoff = shot 3, behind text): list only photos
 * that work there. Fewer than 3 is fine, the list wraps.
 */

export type Brief = {
  tagline?: { fr: string; en: string };
  shots?: number[];
  /** Brand colour override (hex), when the logo measurement is off. */
  color?: string;
  /** Consumer-facing site for the CTA, when the backend domain isn't one. */
  site?: string;
};

export const BRIEFS: Record<string, Brief> = {
  // "Bouillottes de soin aux graines de lin. Made in France. Soin naturel et durable."
  accalmie: {
    tagline: {
      fr: "Bouillottes de soin aux graines de lin",
      en: "Flaxseed comfort heat packs",
    },
    shots: [1, 2],
  },
  // "Une crème de soin pour les ongles. Composée à 99,5 % d'ingrédients d'origine naturelle :
  //  répare, renforce et nourrit les ongles abîmés."
  "care-by-claudette": {
    tagline: {
      fr: "La crème qui répare tes ongles",
      en: "The cream that repairs your nails",
    },
    shots: [2, 1, 3],
  },
  // "Des soins dermo-botaniques fabriqués en France et inspirés par la biodiversité colombienne."
  comblee: {
    tagline: {
      fr: "Soins dermo-botaniques, fabriqués en France",
      en: "Dermo-botanical skincare, made in France",
    },
    shots: [1, 3, 2],
  },
  // No description; the logo reads "Vêtements spirituels".
  "divine-harmonie": {
    tagline: { fr: "Vêtements spirituels", en: "Spiritual clothing" },
    shots: [5, 4, 1],
  },
  // "Emy, la marque dédiée à la santé intime des femmes. […] Conçus avec des professionnels de santé."
  // Shot 2 is a banner with burned-in copy; 1 and 4 are body close-ups: fine on
  // the brand page, not behind a "your link made a sale" notification.
  fizimed: {
    tagline: {
      fr: "La santé intime des femmes",
      en: "Women's intimate health",
    },
    shots: [3, 5],
  },
  // "Duos de soin In&Out : sérums visage et comprimés à croquer. Fabriqué en France."
  "harmo-skincare": {
    tagline: {
      fr: "Duos de soin In&Out, fabriqués en France",
      en: "In & Out skincare duos, made in France",
    },
  },
  // "JE propose des soins minimalistes et vivants, à la croisée de la nature, de la science et du geste."
  je: {
    tagline: {
      fr: "Des soins minimalistes et vivants",
      en: "Minimalist, living skincare",
    },
    shots: [1, 3, 2],
  },
  // "LOULENN révèle les incroyables bienfaits de laine de moutons dans une couette
  //  extraordinaire : thermorégulatrice, respirante et naturellement anti-acariens."
  loulenn: {
    tagline: {
      fr: "La couette en laine qui respire",
      en: "The wool duvet that breathes",
    },
    shots: [3, 2, 5],
  },
  // "NOWA, la douche sans eau."
  "nowa-no-water": {
    tagline: { fr: "La douche sans eau", en: "The waterless shower" },
    shots: [1, 2],
  },
  // "Des soins cosmétiques innovants qui […] soutiennent la longévité cutanée."
  oolution: {
    tagline: {
      fr: "Des soins pour la longévité de ta peau",
      en: "Skincare for your skin's longevity",
    },
  },
  // "Sacs et accessoires élégants, fabriqués en France […] à partir de matières issues de l'upcycling."
  "saint-lazare": {
    tagline: {
      fr: "Sacs upcyclés, fabriqués en France",
      en: "Upcycled bags, made in France",
    },
    shots: [1, 4, 5],
  },
};
